import React, { useEffect, useRef, useState } from 'react'
import JsBarcode from 'jsbarcode'

/**
 * Renders a scannable barcode into an inline SVG.
 *
 * Format is picked from the value itself: 13 digits => EAN13, 12 => UPC, 8 =>
 * EAN8, anything else falls back to CODE128 (which encodes any ASCII). JsBarcode
 * throws on values a format cannot represent, so an invalid barcode degrades to
 * the plain number rather than breaking the row.
 */
export const resolveFormat = (value) => {
  const text = String(value || '')

  if (/^\d{13}$/.test(text)) {
    return 'EAN13'
  }
  if (/^\d{12}$/.test(text)) {
    return 'UPC'
  }
  if (/^\d{8}$/.test(text)) {
    return 'EAN8'
  }

  return 'CODE128'
}

const Barcode = ({ value, height = 34, width = 1.4 }) => {
  const svgRef = useRef(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!value || !svgRef.current) {
      return
    }

    try {
      JsBarcode(svgRef.current, String(value), {
        format: resolveFormat(value),
        height,
        width,
        margin: 0,
        displayValue: true,
        fontSize: 11,
        textMargin: 1,
        background: 'transparent',
      })
      setFailed(false)
    } catch (e) {
      setFailed(true)
    }
  }, [value, height, width])

  if (!value) {
    return <small className="text-muted">-</small>
  }

  if (failed) {
    // Unencodable value — still show the number so the row stays useful.
    return (
      <small className="text-muted item-barcode-fallback">
        {String(value)}
      </small>
    )
  }

  return (
    <svg
      ref={svgRef}
      className="item-barcode-svg"
      data-testid="packlist-item-barcode-svg"
      role="img"
      aria-label={String(value)}
    />
  )
}

export default Barcode
